import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  Divider,
  Flex,
  IconButton,
  Link,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { File, LendingNetwork, Maybe } from '../../gql/generated/graphql';

import PDFViewer from '../PDFViewer.tsx';
import Modal from '../shared/Modal';

import { DealMediaTypeLabels } from '../../constants/media';

type DocumentsTableProps = {
  infoText: string;
  files: Maybe<File>[];
  selectedFilesIndexes: number[];
  setSelectedFilesIndexes: Dispatch<SetStateAction<number[]>>;
  modal: 'generate' | 'upload' | 'sign';
  showDownloadColumn?: boolean;
  selectAllByDefault?: boolean;
  numDocsInProvider?: number;
  providerName?: LendingNetwork;
  numExistingFiles?: number;
  existingFiles?: string[];
};

const DocumentsTable = ({
  infoText,
  files,
  selectedFilesIndexes,
  setSelectedFilesIndexes,
  showDownloadColumn = false,
  selectAllByDefault = false,
  modal,
  numDocsInProvider = 0,
  providerName = LendingNetwork.R1,
  numExistingFiles = 0,
  existingFiles = [],
}: DocumentsTableProps) => {
  const previewModal = useDisclosure();
  const [fileUrl, setFileUrl] = useState<Maybe<string>>();
  const [fileName, setFileName] = useState<Maybe<string>>();

  const columnsWidth = showDownloadColumn
    ? {
        checkbox: '5%',
        document: '35%',
        filename: '45%',
        download: '15%',
      }
    : {
        checkbox: '5%',
        document: '45%',
        filename: '50%',
      };

  const WarningMessage = () => {
    if (!numDocsInProvider && !numExistingFiles) {
      return null;
    }

    switch (modal) {
      case 'generate':
        return (
          <Text fontStyle="italic" color="errorsRed" mb={5}>
            {numExistingFiles !== 1
              ? `There are (${numExistingFiles}) selected files with duplicate file names in the Media Center. Would you like to replace these files with the newly generated ones?`
              : `There is (${numExistingFiles}) selected file with a duplicate file name in the Media Center. Would you like to replace this file with the newly generated one?`}
          </Text>
        );
      case 'upload':
        return (
          <Text fontStyle="italic" color="errorsRed" mb={5}>
            {`There ${
              numDocsInProvider !== 1 ? 'are' : 'is'
            } currently (${numDocsInProvider}) document${
              numDocsInProvider !== 1 ? 's' : ''
            } uploaded to ${providerName}`}
          </Text>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!files || !selectAllByDefault) {
      return;
    }

    const allFilesIndexes = Array.from({ length: files.length }, (_, index) => index);
    setSelectedFilesIndexes(allFilesIndexes);
  }, [files]);

  const handleCheckboxClick = (index: number) => {
    if (selectedFilesIndexes.includes(index)) {
      setSelectedFilesIndexes((previousIndexes) =>
        previousIndexes.filter((fileIndex) => fileIndex !== index),
      );
      return;
    }

    setSelectedFilesIndexes((previousIndexes) => [...previousIndexes, index]);
  };

  const onOpenFile = (file: Maybe<File>) => {
    if (!file) {
      toast.error('Error opening file');
      return;
    }

    setFileUrl(file.url);
    setFileName(file.filename);
    previewModal.onOpen();
  };

  return (
    <>
      <Box>
        <Box>{infoText}</Box>
        <WarningMessage />
        <Flex px={1}>
          <Text w="5%" display="flex" justifyContent="center" alignItems="center">
            <Checkbox
              m={0}
              isChecked={selectedFilesIndexes.length === files.length}
              onChange={() => {
                if (selectedFilesIndexes.length === files.length) {
                  setSelectedFilesIndexes([]);
                  return;
                }

                const allFilesIndexes = Array.from({ length: files.length }, (_, index) => index);
                setSelectedFilesIndexes(allFilesIndexes);
              }}
            />
          </Text>
          <Text w={columnsWidth.document} variant="inbox" m={1}>
            Document
          </Text>
          <Text w={columnsWidth.filename} variant="inbox" m={1}>
            Filename
          </Text>
          {showDownloadColumn ? (
            <Text w={columnsWidth.download} whiteSpace="nowrap" m={1}>
              Download
            </Text>
          ) : null}
        </Flex>
        <Divider mb={0} />
        {files.map((file, index) => (
          <Flex key={file?.key} px={1} _hover={{ bgColor: 'gray.100' }}>
            <Text w="5%" display="flex" justifyContent="center" alignItems="center">
              <Checkbox
                m={0}
                isChecked={selectedFilesIndexes.includes(index)}
                onChange={() => handleCheckboxClick(index)}
              />
            </Text>
            <Text w={columnsWidth.document} variant="inbox" m={1}>
              {file?.mediaType ? DealMediaTypeLabels[file.mediaType] : ''}
            </Text>
            <Text w={columnsWidth.filename} variant="inbox" m={1}>
              <Link
                color={
                  file?.filename && existingFiles.includes(file.filename)
                    ? 'errorsRed'
                    : 'secondary'
                }
                onClick={() => onOpenFile(file)}
              >{`${file?.filename}`}</Link>
            </Text>
            {showDownloadColumn ? (
              <Text
                w={columnsWidth.download}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <IconButton
                  variant="iconHover"
                  size="xs"
                  aria-label="Download"
                  // eslint-disable-next-line security/detect-non-literal-fs-filename
                  onClick={() => window.open(file?.url ?? '', 'PRINT')}
                >
                  <FaDownload />
                </IconButton>
              </Text>
            ) : null}
          </Flex>
        ))}
      </Box>

      <Modal closeOnEsc={false} title={`Previewing: ${fileName}`} size="full" {...previewModal}>
        <PDFViewer fileUrl={fileUrl} />
      </Modal>
    </>
  );
};

export default DocumentsTable;
